import {
  LdSelect,
  LdIcon,
  LdOption,
  LdInput,
  LdLabel,
  LdRadio,
} from "@emdgroup-liquid/liquid/dist/react";
import { FC, useEffect, useMemo, useState } from "react";
import { AnswerOptions } from "typings/api/types";
import { QuestionType } from "typings/app/types";

import { ThumbsDownIcon, ThumbsUpIcon } from "components/Icons";

import "./styles.scss";
import { nanoid } from "nanoid";

interface Props {
  questionText: string;
  questionType: string;
  options: AnswerOptions[];
  mandatory?: boolean;
  disabled?: boolean;
  onChange: (arg0: {
    label?: string;
    value: string | number | boolean | string[];
  }) => void;
}

interface InputProps {
  disabled?: boolean;
  onChange?: (arg0: {
    label?: string;
    value: string | number | boolean | string[];
  }) => void;
}

interface StarInputProps extends InputProps {
  value?: number;
  interactive?: boolean;
}

interface QuestionProps extends InputProps {
  options: AnswerOptions[];
}

export const ThumbRating: FC<InputProps> = ({ onChange, disabled }) => {
  const [value, setValue] = useState<boolean | null>(null);
  const changeState = (val: boolean) => {
    setValue((state) => (state === val ? null : val));
  };
  useEffect(() => {
    if (value !== null && onChange !== undefined) onChange({ value });
  }, [value]);
  return (
    <div className="thumbs-rating flex gap-8">
      <LdIcon
        className={`mt-1 thumbs-icon thumbs-down ${
          value === false ? "active" : ""
        }`}
        onClick={disabled ? undefined : () => changeState(false)}
        size="lg"
      >
        <ThumbsDownIcon />
      </LdIcon>
      <LdIcon
        className={`thumbs-icon  thumbs-up ${value === true ? "active" : ""}`}
        onClick={disabled ? undefined : () => changeState(true)}
        size="lg"
      >
        <ThumbsUpIcon />
      </LdIcon>
    </div>
  );
};
export const StarRating: FC<StarInputProps> = ({
  onChange,
  interactive = true,
  disabled,
  value: initialValue = 0,
}) => {
  const [value, setValue] = useState(initialValue);
  const [hovered, setHovered] = useState(0);
  const handleChangeValue = (val: number) => {
    setHovered(0);
    setValue((state) => (state === val ? 0 : val));
  };
  const rounded = useMemo(() => {
    return parseFloat((Math.round(value * 2) / 2).toFixed(1));
  }, [value]);

  const isHalf = (index: number) => {
    return rounded > index && index === Math.floor(value);
  };

  useEffect(() => {
    if (value !== undefined && onChange !== undefined) onChange({ value });
  }, [value]);

  const buttons = (steps: number) => {
    const b: JSX.Element[] = [];
    for (let i = 1; i <= steps; i++) {
      b.push(
        <div className="star-icon-wrapper">
          <LdIcon size="lg" name={"star"} className="star-icon" />
          <LdIcon
            size="lg"
            onClick={
              interactive && !disabled ? () => handleChangeValue(i) : undefined
            }
            onMouseEnter={
              interactive && !disabled ? () => setHovered(i) : undefined
            }
            onMouseLeave={
              interactive && !disabled ? () => setHovered(0) : undefined
            }
            name={isHalf(i) ? "half-star" : "star"}
            className={`star-icon ${value >= i ? "active" : ""} ${
              hovered >= i ? "hovered" : ""
            }`}
          />
        </div>
      );
    }
    return b;
  };
  return (
    <div className="star-rating flex flex-row gap-4 md:gap-1">{buttons(5)}</div>
  );
};

const likertClasses = {
  form: (cols: number = 5) => `grid grid-cols-${cols} gap-y-4 w-full`,
};

const LiekertScale: FC<QuestionProps> = ({ options, onChange, disabled }) => {
  const [checked, setChecked] = useState<string>();
  const [value, setValue] = useState<number>();
  const [label, setLabel] = useState<string>();
  const prefix = nanoid();

  useEffect(() => {
    if (
      checked !== undefined &&
      value !== undefined &&
      onChange !== undefined
    ) {
      onChange({
        label: checked,
        value,
      });
    }
  });
  return (
    <>
      {/* Placeholder elements to import Tailwind classes*/}
      <div className="grid-cols-3 grid-cols-4 grid-cols-5 grid-cols-6 hidden"></div>
      <form className={likertClasses.form(Object.keys(options).length)}>
        {options.map((option) => (
          <LdLabel
            key={`${prefix}_label_${option.value}`}
            className="text-center"
          >
            <span className="font-normal text-body-xs">{option.label}</span>
          </LdLabel>
        ))}
        {options.map((option) => (
          <span className="text-center" key={`${prefix}_radio_${option.value}`}>
            <LdRadio
              disabled={disabled}
              checked={checked === `${option.value}`}
              value={`${option.value}`}
              onInput={(ev) => {
                setChecked((ev.target as HTMLLdInputElement).value);
                setValue(parseInt(`${option.value}`));
                setLabel(option.label);
              }}
            />
          </span>
        ))}
      </form>
    </>
  );
};

const QuestionForm: FC<Props> = ({
  questionText,
  questionType,
  mandatory,
  options,
  disabled,
  onChange,
}) => {
  const [value, setValue] = useState<string | number | boolean | string[]>();
  const [label, setLabel] = useState<string>();
  useEffect(() => {
    if (value !== undefined) onChange({ value, label });
  }, [value, label]);
  return (
    // <div className="flex items-start py-ld-16 flex-col md:flex-row">
    <div className="grid grid-cols-1 md:grid-cols-2 py-ld-16">
      <LdLabel position="left" className="flex pb-ld-16">
        <span className={`flex pr-ld-32 w-max ${mandatory ? "required" : ""}`}>
          {questionText}
        </span>
      </LdLabel>
      <div className="flex w-full justify-center md:self-center">
        {questionType === "likert-scale" && (
          <LiekertScale
            disabled={disabled}
            onChange={(answer) => {
              setValue(answer.value);
              setLabel(answer.label);
            }}
            options={options}
          />
        )}
        {questionType === "free-text" && (
          <LdInput
            disabled={disabled}
            className="w-full"
            onInput={(ev) => setValue((ev.target as HTMLLdInputElement).value)}
            multiline
            rows={3}
          />
        )}
        {questionType === "star-rating" && (
          <StarRating
            disabled={disabled}
            onChange={(answer) => setValue(answer.value)}
          />
        )}
        {questionType === "thumb-rating" && (
          <ThumbRating
            disabled={disabled}
            onChange={(answer) => setValue(answer.value)}
          />
        )}
        {(questionType === QuestionType.SELECT ||
          questionType === QuestionType.MULTI_SELECT) && (
          <LdSelect
            className="w-full"
            onInput={
              ((
                event: React.SyntheticEvent<
                  HTMLInputElement,
                  CustomEvent<string[]>
                >
              ) => {
                if (questionType === QuestionType.MULTI_SELECT) {
                  setValue(event.nativeEvent.detail);
                } else {
                  setValue(event.nativeEvent.detail[0]);
                }
              }) as unknown as any
            }
            placeholder="Select"
            multiple={questionType === QuestionType.MULTI_SELECT}
          >
            {options.map((option) => {
              return (
                <LdOption
                  key={`${option.value}`}
                  value={`${option.value}`}
                  selected={value === option.value}
                >
                  {option.label}
                </LdOption>
              );
            })}
          </LdSelect>
        )}
      </div>
    </div>
  );
};

export default QuestionForm;
